import { CSSProperties, useEffect, useRef, useState } from 'react'

import { useWallpaperStore } from 'shared/stores/wallpaperStore'

import { getAdaptiveTextColor } from '../../../utils'

type TextColorMode = 'light' | 'dark'

const TEXT_COLORS: Record<
  TextColorMode,
  { primary: string; secondary: string }
> = {
  light: {
    primary: 'rgba(255, 255, 255, 1)',
    secondary: 'rgba(255, 255, 255, 0.7)'
  },
  dark: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.6)'
  }
}

/**
 * Picks light/dark item text that contrasts with the wallpaper region the item
 * sits on, recomputing when the item moves or the wallpaper changes. Returns
 * the ref to attach to the item element and the CSS custom properties to apply.
 */
export const useAdaptiveTextColor = (row: number, col: number) => {
  const gridItemRef = useRef<HTMLDivElement>(null)
  const [mode, setMode] = useState<TextColorMode>('light')
  const wallpaperUrl = useWallpaperStore(state => state.currentWallpaper.image)

  useEffect(() => {
    const updateTextColor = async () => {
      if (!gridItemRef.current || !wallpaperUrl) return
      const result = await getAdaptiveTextColor(
        gridItemRef.current,
        wallpaperUrl
      )
      setMode(result.mode)
    }

    // Small delay to ensure mount before measuring the wallpaper.
    const timeoutId = setTimeout(updateTextColor, 50)
    window.addEventListener('resize', updateTextColor)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('resize', updateTextColor)
    }
  }, [row, col, wallpaperUrl])

  const textColorStyle = {
    '--adaptive-text-color': TEXT_COLORS[mode].primary,
    '--adaptive-text-color-secondary': TEXT_COLORS[mode].secondary
  } as CSSProperties

  return { gridItemRef, textColorStyle }
}
